import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, message, Popover } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useCurrentSimDataId } from '../../../hooks/SimViewer/useCurrentSimDataId';
import { useFeatureOptionsData } from '../../../hooks/data/useFeatureOptionsData';
import { useSummaryData } from '../../../hooks/data/useSummaryData';
import { useRawData } from '../../../hooks/data/useRawData';
import { downloadAsCsv } from '../../../utils/file';
import { LoadingMessage } from '../../../components/LoadingMessage';

const MAX_DOWNLOADABLE_AGENT_COUNT = 300000;

const DownloadButton = (): ReactNode => {
  const { t } = useTranslation();
  const { currentSimDataId } = useCurrentSimDataId();
  const { agentCount } = useSummaryData();
  const { featureOptions } = useFeatureOptionsData();

  const { isLoading, fetchRawData } = useRawData();

  // 設定で無効になっている場合、またはデータの件数が多すぎる場合はダウンロード不可とする
  const canDownload =
    featureOptions?.enable_download &&
    !!agentCount &&
    agentCount <= MAX_DOWNLOADABLE_AGENT_COUNT;

  const onDownload = (): void => {
    if (!currentSimDataId || !canDownload || isLoading) {
      return;
    }

    fetchRawData()
      .then((data: string | null): void => {
        if (data) {
          downloadAsCsv(data, 'data.csv');
        } else {
          message.info(t('message.downloadCancelled'));
        }
      })
      .catch((): void => {
        message.error(t('message.unexpectedError'));
      });
  };

  return (
    <>
      {isLoading && <LoadingMessage message={t('downloading')} />}
      <Popover
        content={
          canDownload
            ? t('message.downloadAnalysisResult')
            : t('message.cannotDownload')
        }
        placement="bottomLeft"
        trigger="hover"
      >
        <StyledButton
          type="text"
          onClick={onDownload}
          disabled={!canDownload || isLoading}
        >
          <DownloadOutlined />
          {t('download')}
        </StyledButton>
      </Popover>
    </>
  );
};

const StyledButton = styled(Button)`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;

  &.ant-btn > span {
    margin: 0;
  }

  &.ant-btn[disabled] {
    color: #646464;
  }
`;

export { DownloadButton };
